import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_MEMBERS_QUERY_NAME, CREATE_MEMBER } from '../../graphql/members';
import useExtendedState from '../../hooks/useExtendedState';
import TextField from '../fields/TextField';
import SaveButton from '../buttons/SaveButton';
import useMultiValidator from '../../hooks/useMultiValidator';
import {
    BALANCING_GROUP_LENGTH,
    MEMBER_EMAILS_MAX_LENGTH,
    MEMBER_ID_LENGTH,
    MEMBER_NAME_MAX_LENGTH,
    MEMBER_PHONE_MAX_LENGTH,
    memberBalancingGroupValidator,
    memberEmailsValidator,
    memberIdValidator,
    memberNameValidator,
    memberPhoneValidator,
} from '../../validators/members';
import { memberPropertiesType } from '../../types';
import SelectField from '../fields/SelectField';
import CheckboxList from '../fields/CheckboxList';
import CheckboxField from '../fields/CheckboxField';
import './_styles_/MemberCreateTab.css';
import {
    AUCTIONEER_AND_TECH_AUCTIONEER,
    getMemberType,
    MEMBER,
    TECHNICAL_AUCTIONEER,
} from '../../utils/MemberTypes';
import ExpandedListTab from '../ExpandedListTab';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

MemberCreateTab.propTypes = {
    memberProps: memberPropertiesType.isRequired,
    hideHandler: PropTypes.func.isRequired,
};

function MemberCreateTab(props) { // NOSONAR javascript:S3776
    const {
        memberProps,
        hideHandler,
    } = props;
    const { t } = useTranslation();
    const {
        availableMemberTypes,
        availableZones,
        availableProductTypes,
        availablePaymentCurrencies,
        clearingEnabled,
        euAuctioneersEnabled,
    } = memberProps;
    const isOnlyEnergyProductAvailable = availableProductTypes.includes('ENERGY') && availableProductTypes.length === 1;
    const memberIdState = useExtendedState('');
    const nameState = useExtendedState('');
    const emailsState = useExtendedState('');
    const phoneState = useExtendedState('');
    const balancingGroupState = useExtendedState(availableProductTypes.includes('ENERGY') ? '' : null);
    const memberTypeState = useExtendedState(getMemberType(availableMemberTypes[0]));
    const paymentCurrencyCodeState = useExtendedState(
        clearingEnabled && availablePaymentCurrencies.length > 0
            ? availablePaymentCurrencies[0].code
            : null,
    );
    const foreignCurrencyAllowedState = useExtendedState(clearingEnabled ? false : null);
    const euAuctioneerState = useExtendedState(euAuctioneersEnabled ? false : null);
    const zonesState = useExtendedState(availableZones.length === 1 ? [availableZones[0].code] : []);
    const availableZonesMap = memberProps.availableZones.map((availableZone) => ({ id: availableZone.code, label: availableZone.name }));
    const setNewMemberType = (newMemberType) => {
        paymentCurrencyCodeState.reset();
        foreignCurrencyAllowedState.reset();
        euAuctioneerState.reset();
        memberTypeState.setValue(getMemberType(newMemberType));
    };
    const { validators, isValid } = useMultiValidator(false, {
        memberId: compositeValidator(memberIdValidator(t), wafValidator(t)),
        name: compositeValidator(memberNameValidator(t), wafValidator(t)),
        balancingGroup: compositeValidator(memberBalancingGroupValidator(t, isOnlyEnergyProductAvailable), wafValidator(t)),
        emails: compositeValidator(memberEmailsValidator(t), wafValidator(t)),
        phone: memberPhoneValidator(t),
    }, {
        emails: true,
        phone: true,
        balancingGroup: !isOnlyEnergyProductAvailable,
    });
    const [isDisabled, setIsDisabled] = useState(true);
    isValid()
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));
    const selectAllZonesHandler = (isSelectAllZones) => {
        let newSelectedZones = [];
        if (isSelectAllZones) {
            newSelectedZones = availableZonesMap
                .map((zone) => zone.id);
        }
        zonesState.setValue(newSelectedZones);
    };

    const extendedMutation = useExtendedMutation(CREATE_MEMBER, {
        variables: {
            memberId: memberIdState.value,
            name: nameState.value,
            memberType: memberTypeState.value.serverType,
            balancingGroup: balancingGroupState.value,
            emails: emailsState.value,
            phone: phoneState.value,
            paymentCurrency: clearingEnabled && memberTypeState.value.is(MEMBER)
                ? paymentCurrencyCodeState.value
                : null,
            foreignCurrencyAllowed: clearingEnabled && (memberTypeState.value.is(TECHNICAL_AUCTIONEER) || memberTypeState.value.is(AUCTIONEER_AND_TECH_AUCTIONEER))
                ? foreignCurrencyAllowedState.value
                : null,
            euAuctioneer: euAuctioneersEnabled && (memberTypeState.value.is(TECHNICAL_AUCTIONEER) || memberTypeState.value.is(AUCTIONEER_AND_TECH_AUCTIONEER))
                ? euAuctioneerState.value
                : null,
            zones: zonesState.value,
        },
        successCallback: hideHandler,
        successMessageOptions: { key: 'members.notification.member-create-success', dataProperty: 'createMember' },
        failureMessageOptions: { key: 'members.notification.member-create-failure', params: { name: nameState.value, memberId: memberIdState.value } },
        refetchQueries: [ALL_MEMBERS_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="member-create-tab"
            title={t('members.member-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <TextField
                    inputId="member-create--member-id"
                    label={t('members.member-id')}
                    state={memberIdState}
                    className="member-create-tab--fields--member-id"
                    validator={validators.memberId}
                    maxLength={MEMBER_ID_LENGTH}
                    placeholder={t('members.placeholder.member-id')}
                    isRequired
                />
                <TextField
                    inputId="member-create--name"
                    label={t('members.name')}
                    state={nameState}
                    className="member-create-tab--fields--name x-wide"
                    validator={validators.name}
                    maxLength={MEMBER_NAME_MAX_LENGTH}
                    placeholder={t('members.placeholder.name')}
                    isRequired
                />
                {availableMemberTypes.length > 1 && (
                    <SelectField
                        inputId="member-create-tab--member-type"
                        label={t('members.member-type')}
                        state={memberTypeState}
                        value={memberTypeState.value.serverType}
                        onChange={setNewMemberType}
                        className="member-create-tab--fields--member-type"
                        options={availableMemberTypes.map((availableMemberType) => (
                            <option value={availableMemberType} key={availableMemberType}>{getMemberType(availableMemberType).translate(t)}</option>
                        ))}
                        isRequired
                    />
                )}
                {availableProductTypes.includes('ENERGY') && (
                    <TextField
                        inputId="member-create--balancing-group"
                        label={t('members.balancing-group')}
                        state={balancingGroupState}
                        className="member-create-tab--fields--balancing-group wide"
                        validator={validators.balancingGroup}
                        maxLength={BALANCING_GROUP_LENGTH}
                        placeholder={t('members.placeholder.balancing-group')}
                        isRequired={isOnlyEnergyProductAvailable}
                    />
                )}
                <TextField
                    inputId="member-create--emails"
                    label={t('members.emails')}
                    state={emailsState}
                    className="member-create-tab--fields--emails x-wide"
                    validator={validators.emails}
                    maxLength={MEMBER_EMAILS_MAX_LENGTH}
                    inputMode="email"
                    placeholder={t('members.placeholder.emails')}
                />
                <TextField
                    inputId="member-create--phone"
                    label={t('members.phone')}
                    state={phoneState}
                    className="member-create-tab--fields--phone"
                    validator={validators.phone}
                    maxLength={MEMBER_PHONE_MAX_LENGTH}
                    inputMode="tel"
                    placeholder={t('members.placeholder.phone')}
                />
                {clearingEnabled && memberTypeState.value.is(MEMBER) && (
                    <SelectField
                        inputId="member-create--payment-currency"
                        label={t('members.payment-currency')}
                        state={paymentCurrencyCodeState}
                        className="member-create-tab--fields--payment-currency"
                        options={availablePaymentCurrencies.map(({ code: availablePaymentCurrencyCode }) => (
                            <option value={availablePaymentCurrencyCode} key={availablePaymentCurrencyCode}>{availablePaymentCurrencyCode}</option>
                        ))}
                        isRequired
                    />
                )}
                {clearingEnabled && (memberTypeState.value.is(TECHNICAL_AUCTIONEER) || memberTypeState.value.is(AUCTIONEER_AND_TECH_AUCTIONEER)) && (
                    <CheckboxField
                        inputId="member-create--foreign-currency-allowed"
                        label={t('members.foreign-currency-allowed')}
                        state={foreignCurrencyAllowedState}
                        className="member-create-tab--fields--foreign-currency-allowed"
                    />
                )}
                {euAuctioneersEnabled && (memberTypeState.value.is(TECHNICAL_AUCTIONEER) || memberTypeState.value.is(AUCTIONEER_AND_TECH_AUCTIONEER)) && (
                    <CheckboxField
                        inputId="member-create--eu-auctioneer"
                        label={t('members.eu-auctioneer')}
                        state={euAuctioneerState}
                        className="member-create-tab--fields--eu-auctioneer"
                    />
                )}
            </div>
            {availableZones.length > 1 && (
                <div className="member-create--zones">
                    <CheckboxField
                        label={t('members.zones-select-all')}
                        className="member-create-tab--fields--zones-select-all"
                        onChange={selectAllZonesHandler}
                        inputId="member-create-tab--fields--zones-select-all"
                    />
                    <CheckboxList
                        label={t('members.zones')}
                        name="zones-list"
                        values={availableZonesMap}
                        state={zonesState}
                        className="member-create-tab--fields--zones"
                    />
                </div>
            )}
            <div className="rich-list-row-expanded--buttons">
                <SaveButton extendedMutation={extendedMutation} disabled={isDisabled} confirmationMessage={t('members.confirm-create-member', { name: nameState.value, memberId: memberIdState.value })} />
            </div>
        </ExpandedListTab>
    );
}

export default MemberCreateTab;
